/**
 * The collection display mode a player actually draws, which is not always the one it was given.
 *
 * `bottom-bar` and `sidebar` are chosen once, on a collection's embed, for every screen it will be
 * seen on. On a phone they leave the video almost nothing: a 280px sidebar beside a 375px frame, a
 * strip taking most of a 16:9 iframe's height. **Below {@link NARROW_COLLECTION_PX} the player
 * falls back to `drawer`**, whose list opens from the control bar over the video, and goes back to
 * the chosen mode when the room comes back.
 *
 * Measured on the element that holds the video *and* its list, whose width does not depend on the
 * mode: measuring the video alone would change with the switch it decides, and flap.
 */

import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue'
import { useElementSize } from '@vueuse/core'
import type { CollectionDisplayMode } from '../types'

/** Under this width, a strip or a sidebar beside the video costs more than it shows. */
export const NARROW_COLLECTION_PX = 600

export function useEffectiveDisplayMode(
  mode: MaybeRefOrGetter<CollectionDisplayMode | undefined>,
  container: Ref<HTMLElement | null | undefined>,
) {
  // Before the first measurement the width is 0, which would start every player in the drawer and
  // then switch: the chosen mode is kept until there is a width to judge it by.
  const { width } = useElementSize(container, undefined, { box: 'border-box' })

  return computed<CollectionDisplayMode | undefined>(() => {
    const chosen = toValue(mode)
    if (!chosen || chosen === 'drawer') return chosen
    return width.value > 0 && width.value < NARROW_COLLECTION_PX ? 'drawer' : chosen
  })
}
